// @ts-strict-ignore
import { GetMassFiatRecordsProps } from 'hooks/queries/MassPayouts/useGetMassFiatPayoutsRecords'
import { LegalAgreementStatusType } from 'pages/LegalAgreements/types'
import { TransactionsRequest } from 'services/TransactionsService'
import {
  BeneficiaryEntityType,
  ValidateIbanRequest,
  ValidateScanRequest,
} from 'types/beneficiaries'
import { BulkCapabilityRequest, CapabilitiesScope } from 'types/capabilities/capabilities'
import {
  ContactEntityType,
  ContactsRequest,
  InstrumentType,
  RelationshipType,
} from 'types/contacts'
import {
  CustomersRequest,
  CustomerWalletsRequest,
  GetCustomerWalletProfilesRequest,
} from 'types/customers'
import {
  LedgerWalletStatus,
  LedgerWalletTransactionsRequestParams,
} from 'types/ledger/ledgerWalletTransactions'
import type { LimitsQueryParams } from 'types/paymentLimits'
import { PaymentCaseType } from 'types/payments'
import { ConfirmPayeeRequest } from 'types/payoutsV2'
import { GetChannelsReq } from 'types/transaction/GetChannelsReq'
import { GetWalletTransactionsReq } from 'types/transaction/GetTransactionsReq'
import { Paging } from 'types/types'
import { TransferWalletsRequest } from 'types/unifiedPayouts/unifiedPayouts'
import { UnifiedPayoutsV2Request } from 'types/unifiedPayouts/unifiedPayoutsV2'
import { TWalletType, WalletsPayRequest } from 'types/wallets'

export const reactQueryName = {
  BENEFICIARIES: 'beneficiaries',
  BULK_CAPABILITIES: 'bulkCapabilities',
  CHANNEL: 'channel',
  CONTACT: 'contact',
  CONTACT_CASES: 'contactCases',
  CONTACT_CASE_COUNT: 'contactCaseCount',
  CONTACT_INSTRUMENTS: 'contactInstruments',
  CONTACT_PAYMENT_INSTRUMENTS: 'contactPaymentInstruments',
  CONTACTS: 'contacts',
  CONTACTS_PAYMENT: 'contactsPayment',
  APPLICATIONS: 'applications',
  CUSTOMER: 'customer',
  CUSTOMER_APPLICATION: 'customerApplication',
  CUSTOMER_EARNINGS_SUMMARY: 'customerEarningsSummary',
  CUSTOMERS: 'customers',
  CUSTOMER_WALLET: 'customerWallet',
  CUSTOMER_WALLET_PROFILES: 'customerWalletProfiles',
  CUSTOMER_WALLETS: 'customerWallets',
  CUSTOMER_FEE_WALLETS: 'customerFeeWallets',
  DESTINATIONS: 'destinations',
  EMBEDDED_CUSTOMER_AGREEMENTS: 'embeddedCustomerAgreements',
  LEDGER_WALLET_TRANSACTIONS: 'ledgerWalletTransactions',
  LEDGER_TRANSACTION_HISTORY: 'ledgerTransactionHistory',
  LEDGER_WALLETS: 'ledgerWallets',
  MONTHLY_CUSTOMERS_EARNINGS: 'monthlyCustomersEarnings',
  PAYMENT: 'payment',
  PAYMENT_LIMITS: 'paymentLimits',
  REWARDS_PORTFOLIOS: 'rewardsPortfolios',
  REWARDS_PRODUCTS: 'rewardsProducts',
  TRANSACTIONS: 'transactions',
  WALLET_CONTACT_PAYMENT_INSTRUMENTS: 'walletContactPaymentInstruments',
} as const

type WalletsV2Arg = {
  walletType: TWalletType
  searchTerm?: string
  offset?: number
  max?: number
  order?: string
  sort?: string
  includeEmbedded?: boolean
  groupBy?: string
}

export const reactQueryKeys = {
  applications: (
    page?: number,
    size?: number,
    name?: string,
    statuses?: string[],
    types?: string[]
  ) => [reactQueryName.APPLICATIONS, page, size, name, statuses, types] as const,
  accountPreferences: () => ['accountPreferences'] as const,
  accountUserGroups: () => ['accountUserGroups'] as const,
  accountUsers: (paging: Paging) => ['accountUsers', paging],
  accounts: () => ['accounts'],
  accountApprovalsPreference: (accountReference: string, preference?: string) => [
    'accountApprovalsPreference',
    accountReference,
    preference,
  ],
  allowedCountries: () => ['allowedCountries'] as const,
  activity: () => ['activity'],
  apiKeys: (query?: string) => ['apiKeys', query] as const,
  approvalsTransactions: (currentAccountReference: string, status: string, pageSize?: number) => [
    'approvalsTransactions',
    currentAccountReference,
    status,
    pageSize,
  ],
  banks: (countryCode: string, currencyCode: string, canEnable: boolean) => [
    'banks',
    countryCode,
    currencyCode,
    canEnable,
  ],
  beneficiaries: (size: number, page: number, sort: string) =>
    [reactQueryName.BENEFICIARIES, size, page, ...(sort && [{ sort }])] as const,
  bvnkNetworkMembers: (accountReference: string) => ['bvnkNetworkMembers', accountReference],
  capabilities: (group: string, name: string, scope: CapabilitiesScope, subjectId: string) =>
    ['capabilities', group, name, scope, subjectId] as const,
  bulkCapabilities: (req?: BulkCapabilityRequest, queryHash?: string) => [
    reactQueryName.BULK_CAPABILITIES,
    req,
    ...(queryHash ? [queryHash] : []),
  ],
  channel: (uuid: string) => ['channel', uuid],
  channels: (req: GetChannelsReq) => ['channels', req],
  channelPayments: (
    paging: Paging,
    from: string,
    to: string,
    merchant: string,
    status: string,
    search: string,
    tag?: string
  ) => ['channelPayments', from, to, merchant, status, search, tag, paging],
  confirmPayee: (request: ConfirmPayeeRequest) => ['confirmPayee', request],
  contact: (id: string) => [reactQueryName.CONTACT, id],
  contactCaseCount: () => ['contactCaseCount'] as const,
  contactCases: (paging: Paging) => ['contactCases', paging],
  contacts: (req?: ContactsRequest) => [reactQueryName.CONTACTS, req] as const,
  contactsPayment: (searchTerm?: string, contactIds?: string[], type?: ContactEntityType) =>
    [
      reactQueryName.CONTACTS_PAYMENT,
      searchTerm,
      ...(contactIds ? [{ contactIds }] : []),
      ...(type ? [{ type }] : []),
    ] as const,
  contactInstruments: (req?: ContactsRequest) => [reactQueryName.CONTACT_INSTRUMENTS, req],
  contactPaymentInstruments: (
    searchTerm?: string,
    paymentMethod?: InstrumentType,
    currency?: string
  ) => [reactQueryName.CONTACT_PAYMENT_INSTRUMENTS, searchTerm, paymentMethod, currency] as const,
  country: () => ['detectCountry'],
  countries: (canEnable: boolean, pagination: boolean) => ['countries', canEnable, pagination],
  countryStates: (countryCode: string) => ['countryStates', countryCode],
  customer: (customerId: string) => [reactQueryName.CUSTOMER, customerId],
  customerApplication: (onboardingId: string) => [
    reactQueryName.CUSTOMER_APPLICATION,
    onboardingId,
  ],
  customers: ({ sort, filters, ...customersParams }: CustomersRequest) =>
    [
      reactQueryName.CUSTOMERS,
      customersParams,
      ...(filters ? [filters] : []),
      ...(sort && [{ sort }]),
    ] as const,
  customerDocuments: (customerReference: string) => ['customer-documents', customerReference],
  customerDocumentLink: (reference: string) => ['customer-document-link', reference],
  customerWallet: (walletId: string) => [reactQueryName.CUSTOMER_WALLET, walletId] as const,
  customerWallets: (req?: CustomerWalletsRequest) =>
    [reactQueryName.CUSTOMER_WALLETS, req] as const,
  customerWalletProfiles: (req?: GetCustomerWalletProfilesRequest) =>
    [reactQueryName.CUSTOMER_WALLET_PROFILES, req] as const,
  customerFeeWallets: () => [reactQueryName.CUSTOMER_FEE_WALLETS] as const,
  currencyPairs: () => ['currencyPairs'] as const,
  cryptoAddresses: () => ['cryptoAddresses'],
  cryptoCurrencies: () => ['cryptoCurrencies'],
  destinations: (size: number, page: number, sort: string, search?: string) =>
    [reactQueryName.DESTINATIONS, size, page, ...(sort && [{ sort }]), ...(search ? [{ search }] : [])] as const,
  depositAccounts: (walletId: string) => ['depositAccounts', walletId],
  exchangeRate: (fromCurrency: string, toCurrency: string, amount: number | string) => [
    'exchangeRate',
    fromCurrency,
    toCurrency,
    amount,
  ],
  events: () => ['events'],
  embeddedCustomerAgreements: (customerId: string) => [
    reactQueryName.EMBEDDED_CUSTOMER_AGREEMENTS,
    customerId,
  ],
  fiatCurrencies: () => ['fiatCurrencies'],
  fiatWithCurrencyCodes: () => ['fiatWithCurrencyCodes'],
  getPaymentMethods: () => ['getPaymentMethods'],
  getWalletLimits: (currencyCode: string, walletId: number) => [
    'getWalletLimits',
    currencyCode,
    walletId,
  ],
  infoRequired: (customerReference: string) => ['info-required', customerReference],
  industries: () => ['industries'],
  ledgerWalletTransactions: (req: LedgerWalletTransactionsRequestParams) => [
    reactQueryName.LEDGER_WALLET_TRANSACTIONS,
    req,
  ],
  ledgerTransactionHistory: (
    req: TransactionsRequest & { searchTerm?: string; aggregation?: string[] }
  ) => [reactQueryName.LEDGER_TRANSACTION_HISTORY, req],
  ledgerWallets: (params: {
    currencyCode?: string
    searchTerm?: string
    size?: number
    page?: number
    start?: string
    end?: string
    includeCustomerWallets?: boolean
    statuses?: LedgerWalletStatus[]
  }) => [reactQueryName.LEDGER_WALLETS, params] as const,
  legalAgreements: (statusParams?: LegalAgreementStatusType[]) => ['legalAgreements', statusParams],
  massFiatPayoutsDetails: (batchReference: string) => ['massFiatPayoutsDetails', batchReference],
  massFiatPayoutsRecords: ({ batchReference, size, page }: GetMassFiatRecordsProps) => [
    'massFiatPayoutsRecords',
    { batchReference, size, page },
  ],
  massFiatPayoutsList: (page?: number, size?: number) => ['massFiatPayoutsList', { page, size }],
  massCryptoPayoutsList: () => ['massCryptoPayoutsList'] as const,
  massCryptoPayoutById: (batchId: string) => ['massCryptoPayoutById', batchId],
  massCryptoPayoutsListByPolling: () => ['massCryptoPayoutsListByPolling'] as const,
  merchants: () => ['merchants'],
  monthlyVolumes: () => ['monthlyVolumes'],
  paymentLimits: (params: LimitsQueryParams) => [reactQueryName.PAYMENT_LIMITS, params] as const,
  payment: (uuid: string) => ['payment', uuid],
  payments: (
    paging: Paging,
    from: string,
    to: string,
    merchant: string,
    status: string,
    searchByRef: string,
    searchByTransactionHash: string,
    searchByUUID: string,
    tag: string
  ) => [
    'payments',
    from,
    to,
    merchant,
    status,
    searchByRef,
    searchByTransactionHash,
    searchByUUID,
    paging,
    tag,
  ],
  paymentCaseDocument: (paymentCaseDocumentId: string) => [
    'paymentCaseDocument',
    paymentCaseDocumentId,
  ],
  paymentCaseDocuments: (paymentCaseId: string) => ['paymentCaseDocuments', paymentCaseId],
  paymentCaseEmails: (paymentCaseId: string) => ['paymentCaseEmails', paymentCaseId],
  payFees: (walletId: number, amount: number) => ['payFees', walletId, amount],
  payoutsFeeEstimate: (request: UnifiedPayoutsV2Request) => ['payoutsFeeEstimate', request],
  paymentSupportCases: (paging?: Paging, status?: PaymentCaseType[]) => [
    'paymentSupportCases',
    paging,
    status,
  ],
  pendingAgreements: () => ['accountAgreements'],
  postPay: (walletId: string, request: WalletsPayRequest) => ['postPay', walletId, request],
  postWalletSearch: req => ['postWalletSearch', req],
  profile: () => ['profile'],
  documentDownload: (url: string) => ['documentDownload', url],
  quote: (uuid: string) => ['uuid', uuid],
  reissueToken: () => ['reissueToken'],
  reportSchedules: (walletId: string | undefined) => ['reportSchedules', walletId],
  reportScheduleTimezones: () => ['reportScheduleTimezones'],
  secrectCode: () => ['secrectCode'],
  supportedCurrencies: () => ['supportedCurrencies'],
  tradingHours: () => ['tradingHours'],
  transactions: (req?: TransactionsRequest) => [reactQueryName.TRANSACTIONS, req] as const,
  transferWallets: (req: TransferWalletsRequest) => ['transferWallets', req] as const,
  totalBalance: (baseCurrency: string) => ['totalBalance', baseCurrency] as const,
  validateBic: (req: string) => ['validateBic', req],
  validateIban: (req: ValidateIbanRequest) => ['validateIban', req],
  validateRoutingNumber: (routingNumber: string) => ['validateRoutingNumber', routingNumber],
  validateScan: (req: ValidateScanRequest) => ['validateScan', req],
  verification: ['verification'] as const,
  verificationRequestStatus: () =>
    [...reactQueryKeys.verification, 'verificationRequestStatus'] as const,
  walletContactPaymentInstruments: (
    walletLsid: string,
    searchTerm?: string,
    relationshipType?: RelationshipType,
    entityType?: BeneficiaryEntityType
  ) =>
    [
      reactQueryName.WALLET_CONTACT_PAYMENT_INSTRUMENTS,
      walletLsid,
      searchTerm,
      relationshipType,
      entityType,
    ] as const,
  wallet: (id: number) => ['wallet', id],
  walletBalances: () => ['walletBalances'] as const,
  walletLegacyId: (walletLsid: string) => ['walletLegacyId', walletLsid],
  wallets: () => ['wallets'] as const,
  walletsV2: ({
    walletType,
    searchTerm,
    offset,
    max,
    order,
    sort,
    includeEmbedded,
    groupBy,
  }: WalletsV2Arg) =>
    [
      'walletsV2',
      walletType,
      searchTerm,
      offset,
      max,
      order,
      sort,
      includeEmbedded,
      groupBy,
    ] as const,
  walletTransactions: (req: GetWalletTransactionsReq) => ['walletTransactions', req],
  walletTransactionDetails: (req: { transactionUUID: string }) => ['walletTransactionDetails', req],
  withdrawalFee: (walletId: number, amount: number) => ['withdrawalFee', walletId, amount] as const,
  southAfricanBanks: () => ['southAfricanBanks'],
  chatbotToken: () => ['chatbotToken'],
  rewardsPortfolios: (pageNumber?: number, pageSize?: number) =>
    [reactQueryName.REWARDS_PORTFOLIOS, pageNumber, pageSize] as const,
  rewardsPortfolio: (portfolioId: string) =>
    [reactQueryName.REWARDS_PORTFOLIOS, portfolioId] as const,
  rewardsProducts: (pageNumber?: number, pageSize?: number, currency?: string) =>
    [reactQueryName.REWARDS_PRODUCTS, pageNumber, pageSize, currency] as const,
  customerEarningsSummary: (currency: string) =>
    [reactQueryName.CUSTOMER_EARNINGS_SUMMARY, currency] as const,
  monthlyCustomersEarnings: (
    currency: string,
    startDate?: string,
    endDate?: string,
    pageNumber?: number,
    pageSize?: number
  ) =>
    [
      reactQueryName.MONTHLY_CUSTOMERS_EARNINGS,
      currency,
      startDate,
      endDate,
      pageNumber,
      pageSize,
    ] as const,
}
